import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Film, Loader2, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail, resolveMediaUrl } from "../lib/api";

const ACCEPT = {
  image: "image/jpeg,image/png,image/webp,image/gif",
  video: "video/mp4,video/webm,video/quicktime",
};

const MAX_SIZE_MB = {
  image: 8,
  video: 80,
};

export default function MediaUpload({
  kind = "image",          // "image" | "video"
  value = "",              // current url
  onChange,                // (newUrl: string) => void
  label,                   // label above the dropzone
  testidPrefix = "media",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const isVideo = kind === "video";
  const Icon = isVideo ? Film : ImageIcon;
  const maxMb = MAX_SIZE_MB[kind];

  const handleFile = async (file) => {
    if (!file) return;
    setError("");

    // Client-side validation
    const accepted = ACCEPT[kind].split(",");
    if (!accepted.includes(file.type)) {
      setError(`Format non supporté. Acceptés : ${accepted.map((t) => t.split("/")[1]).join(", ")}`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setError(`Fichier trop volumineux (max ${maxMb} MB)`);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      setProgress(0);
      const res = await api.post(`/admin/upload/${kind}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      onChange(res.data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const onSelectClick = () => inputRef.current?.click();

  const onClear = () => {
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const fullUrl = value ? resolveMediaUrl(value) : "";

  return (
    <div data-testid={`${testidPrefix}-upload`}>
      {label && (
        <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
          {label}
        </label>
      )}

      {/* Preview */}
      {value && !uploading && (
        <div className="relative mb-3 border border-[#0D2F4C]/15 bg-[#F8F9FA] overflow-hidden">
          {isVideo ? (
            <video
              data-testid={`${testidPrefix}-preview`}
              src={fullUrl}
              controls
              className="w-full max-h-80 bg-black"
            />
          ) : (
            <img
              data-testid={`${testidPrefix}-preview`}
              src={fullUrl}
              alt="preview"
              className="w-full max-h-72 object-contain"
            />
          )}
          <button
            type="button"
            data-testid={`${testidPrefix}-clear-btn`}
            onClick={onClear}
            className="absolute top-2 right-2 bg-white border border-[#0D2F4C]/20 p-1.5 hover:bg-red-50 hover:border-red-300 transition-colors"
            aria-label="Remove"
          >
            <X size={14} className="text-red-600" />
          </button>
        </div>
      )}

      {/* Dropzone (shown when no value OR while uploading) */}
      {(!value || uploading) && (
        <div
          onClick={onSelectClick}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid={`${testidPrefix}-dropzone`}
          className={`cursor-pointer border-2 border-dashed transition-colors p-8 text-center ${
            dragOver
              ? "border-[#0D2F4C] bg-[#0D2F4C]/5"
              : "border-[#0D2F4C]/20 hover:border-[#0D2F4C]/50 bg-[#F8F9FA]"
          } ${uploading ? "pointer-events-none opacity-70" : ""}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={28} className="text-[#0D2F4C] animate-spin" />
              <div className="text-sm text-[#0D2F4C]">Téléchargement... {progress}%</div>
              <div className="w-full max-w-xs h-1 bg-[#0D2F4C]/10">
                <div
                  className="h-full bg-[#0D2F4C] transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-[#0D2F4C]/5 flex items-center justify-center">
                <Icon size={22} className="text-[#0D2F4C]" />
              </div>
              <div className="text-sm text-[#0D2F4C] font-medium mt-1">
                <Upload size={14} className="inline mr-1.5 -mt-0.5" />
                {isVideo ? "Glissez une vidéo ici, ou cliquez pour choisir" : "Glissez une image ici, ou cliquez pour choisir"}
              </div>
              <div className="text-xs text-[#4B5563]">
                {isVideo ? "MP4, WebM, MOV" : "JPG, PNG, WebP, GIF"} · max {maxMb} MB
              </div>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT[kind]}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testidPrefix}-input`}
      />

      {error && (
        <div
          data-testid={`${testidPrefix}-error`}
          className="mt-2 flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 text-red-800 text-xs"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
